import type {
  FunnelAnalyticsEnvelope,
  FunnelAnalyticsEventName,
} from "@src/funnel/hooks/useFunnelAnalytics";

export const GA4_EVENT_NAME_MAP = {
  funnel_landing_viewed: "funnel_landing_viewed",
  funnel_started: "funnel_started",
  funnel_step_viewed: "funnel_step_viewed",
  funnel_answer_selected: "funnel_answer_selected",
  funnel_step_completed: "funnel_step_completed",
  funnel_back_clicked: "funnel_back_clicked",
  funnel_email_submitted: "generate_lead",
  funnel_signup_completed: "sign_up",
  funnel_handoff_clicked: "funnel_handoff_clicked",
  funnel_loading_viewed: "funnel_loading_viewed",
  funnel_character_revealed: "funnel_character_revealed",
  funnel_first_message_previewed: "funnel_first_message_previewed",
  paywall_viewed: "funnel_paywall_viewed",
  plan_selected: "funnel_plan_selected",
  purchase_completed: "purchase",
  funnel_abandoned: "funnel_abandoned",
} as const satisfies Record<FunnelAnalyticsEventName, string>;

const GA4_SAFE_ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "campaign_id",
  "ad_id",
  "adset_id",
  "network",
  "placement",
  "account_id",
  "site_source_name",
  "geo_country",
  "inlet_id",
] as const;

type FunnelAnalyticsEnvelopeWithExtras = FunnelAnalyticsEnvelope & {
  readonly answer_key?: string;
  readonly duration_ms?: number;
  readonly value?: number;
  readonly currency?: string;
};

const isString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const buildBaseParams = (
  envelope: FunnelAnalyticsEnvelopeWithExtras,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    event_id: envelope.event_id,
    session_id: envelope.session_id,
    funnel_id: envelope.funnel_id,
    application_id: envelope.application_id,
    anonymous_id: envelope.anonymous_id,
  };

  if (isString(envelope.override_id)) params.override_id = envelope.override_id;
  if (isString(envelope.experiment_id)) params.experiment_id = envelope.experiment_id;
  if (isString(envelope.arm_id)) params.arm_id = envelope.arm_id;
  if (isNumber(envelope.funnel_version)) params.funnel_version = envelope.funnel_version;
  if (isString(envelope.device_type)) params.device_type = envelope.device_type;
  if (isString(envelope.step_id)) params.step_id = envelope.step_id;
  if (isNumber(envelope.step_index)) params.step_index = envelope.step_index;
  if (isString(envelope.answer_key)) params.answer_key = envelope.answer_key;
  if (isNumber(envelope.duration_ms)) params.duration_ms = envelope.duration_ms;

  const attribution = envelope.attribution as Record<string, string | undefined> | undefined;
  if (attribution && typeof attribution === "object") {
    for (const key of GA4_SAFE_ATTRIBUTION_KEYS) {
      const value = attribution[key];
      if (isString(value)) params[key] = value;
    }
  }

  return params;
};

const buildEcommerceParams = (
  envelope: FunnelAnalyticsEnvelopeWithExtras,
): Record<string, string | number> => {
  if (envelope.event !== "plan_selected" && envelope.event !== "purchase_completed") {
    return {};
  }

  const params: Record<string, string | number> = { transaction_id: envelope.event_id };
  if (isNumber(envelope.value)) params.value = envelope.value;
  if (isString(envelope.currency)) params.currency = envelope.currency;
  return params;
};

export const buildGa4Params = (
  envelope: FunnelAnalyticsEnvelope,
): Record<string, string | number> => ({
  ...buildBaseParams(envelope),
  ...buildEcommerceParams(envelope),
});

export const fireGa4Event = (envelope: FunnelAnalyticsEnvelope): void => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  const eventName = GA4_EVENT_NAME_MAP[envelope.event];
  if (!eventName) return;

  try {
    window.gtag("event", eventName, buildGa4Params(envelope));
  } catch {
    // Analytics must never block funnel progression.
  }
};
